import { inject, Injectable } from '@angular/core';
import { NotificationRef, NotificationService } from '@progress/kendo-angular-notification';
import { DialogRef, DialogService } from '@progress/kendo-angular-dialog';
import { firstValueFrom, map } from 'rxjs';
import { ToastService } from './toast.service';
import { TranslationService } from '../translate-service/translation.service';

@Injectable({
    providedIn: 'root',
})
export class WebToastService extends ToastService {
    private notificationService = inject(NotificationService);
    private dialogService = inject(DialogService);

    async displayToast(
        message: string,
        color: 'success' | 'danger' | 'warning' | 'info' = 'info',
        icon?: string,
        translucent?: boolean,
        duration: number | undefined = 800,
        closable = false,
    ): Promise<void> {
        const style = color === 'success' ? 'success' : color === 'danger' ? 'error' : color === 'warning' ? 'warning' : 'info';

        this.showToast(message, style, closable, duration);
    }

    async showSuccess(message: string): Promise<void> {
        await this.displayToast(message, 'success');
    }

    async showError(message: string): Promise<void> {
        if (this.isRepeatOfStoreOffline(message)) {
            return;
        }
        const style = 'error';
        const notificationRef = this.showToast(message, style, true, undefined);

        setTimeout(() => {
            if (notificationRef) {
                notificationRef.hide();
            }
        }, 10000);
    }

    async showYesOrNoQuestion(title: string, content: string): Promise<boolean> {
        const translationService = this.injector.get(TranslationService);
        const dialog = this.dialogService.open({
            width: '50%',
            title: title,
            content: content,
            actions: [
                { text: translationService.getTranslation('Common', 'Button', 'No'), value: false },
                { text: translationService.getTranslation('Common', 'Button', 'Yes'), themeColor: 'primary', value: true },
            ],
        });

        return await firstValueFrom(
            dialog.result.pipe(
                map((x) => {
                    return (x as { text: string; value: boolean })?.value ?? false;
                }),
            ),
        );
    }

    async showDialog(title: string, content: string, actions: unknown[]): Promise<object> {
        const dialog: DialogRef = this.dialogService.open({
            title: title,
            content: content,
            actions: actions,
        });

        return await firstValueFrom(dialog.result);
    }

    private showToast(
        message: string,
        style: 'success' | 'warning' | 'info' | 'error' | 'none',
        closable: boolean,
        duration: number | undefined,
    ): NotificationRef {
        return this.notificationService.show({
            content: message,
            type: { style: style, icon: true },
            position: { horizontal: 'center', vertical: 'bottom' },
            animation: { type: 'fade', duration: 500 },
            closable: closable,
            hideAfter: duration,
        });
    }
}
